import { Button, Modal } from "@mantine/core";
import { useState } from "react";
import { ModalFooter } from "../../../mantine/components/ModalFooter/ModalFooter";
import {
  BasicConfirmationModal,
  BasicConfirmationModalIcon
} from "../../BuildingBlocks/Layout/Modals/BasicConfirmationModal/BasicConfirmationModal";
import type { OAuthApplicationResponse } from "../ApiSettings.types";
import { OAuthApplicationSuccess } from "../CreateOAuthApplicationModal/OAuthApplicationSuccess/OAuthApplicationSuccess";
import { useOAuthApplicationsMutations } from "../hooks/useOAuthApplicationsMutations";

interface RegenerateClientSecretModalProps {
  application: OAuthApplicationResponse | null;
  isOpen?: boolean;
  onClose: () => void;
}

function RegenerateClientSecretModal({
  application,
  isOpen,
  onClose
}: RegenerateClientSecretModalProps) {
  const { regenerateSecretMutation } = useOAuthApplicationsMutations();
  const [newClientSecret, setNewClientSecret] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [isRegenerating, setIsRegenerating] = useState(false);

  function handleClose() {
    setNewClientSecret(null);
    setError(null);
    onClose();
  }

  async function handleRegenerate() {
    if (!application) {
      return;
    }

    setIsRegenerating(true);

    try {
      const response = await regenerateSecretMutation.mutateAsync(
        application.id
      );
      setNewClientSecret(response.data.client_secret);
    } catch (caughtError) {
      setError(caughtError);
    } finally {
      setIsRegenerating(false);
    }
  }

  if (newClientSecret) {
    return (
      <Modal
        className="RegenerateClientSecretModal"
        opened={isOpen || false}
        title="Client Secret neu generiert"
        onClose={handleClose}
      >
        <OAuthApplicationSuccess
          clientId={application?.client_id ?? ""}
          clientSecret={newClientSecret}
          intro="Das Client Secret wurde erfolgreich neu generiert. Das bisherige Client Secret ist ab sofort ungültig."
        />
        <ModalFooter>
          <Button variant="default" onClick={handleClose}>
            Schließen
          </Button>
        </ModalFooter>
      </Modal>
    );
  }

  return (
    <BasicConfirmationModal
      className="RegenerateClientSecretModal"
      confirmationButtonColor="error"
      confirmationButtonText="Ja, neu generieren"
      error={error}
      icon={BasicConfirmationModalIcon.Error}
      isLoading={isRegenerating}
      opened={isOpen || false}
      title="Client Secret neu generieren"
      toggleModal={handleClose}
      onCancel={handleClose}
      onConfirm={handleRegenerate}
    >
      Möchten Sie das Client Secret für die Applikation{" "}
      <b>{application?.name}</b> wirklich neu generieren?
      <br />
      <br />
      <b>Achtung:</b> Das bisherige Client Secret wird dadurch ungültig und alle
      Integrationen, die es verwenden, funktionieren nicht mehr.
      <br />
      <br />
      Das neue Client Secret wird anschließend einmalig in diesem Dialog
      angezeigt.
    </BasicConfirmationModal>
  );
}

export { RegenerateClientSecretModal, RegenerateClientSecretModalProps };
